// Hotfix: the "Add to Bag" / "Quick Add" buttons on collection product cards
// (class .hxz-cart-btn, inside a <hxz-product-card> element) are wired
// through ReactLoader.loadResource(), a queue-based lazy loader for
// React/react-dom/sku-drawer.js/engraving.js. That loader can get stuck
// (its internal "loading" flag never clears) so the queued callback that
// actually adds the item to the cart never fires - this is the original bug.
//
// This script intercepts the click in the capture phase, stops the broken
// handler from running, and calls the product card's own addToCart()
// method directly instead:
//   - "Add to Bag" (single variant) only needs window.addToCart, which is
//     defined immediately in hxz-custom.js and never depends on the lazy
//     loader, so it can run right away.
//   - "Quick Add" (multiple variants) needs window.quickShop, which lives
//     in the lazily-loaded sku-drawer.js. Rather than trust
//     ReactLoader.loadResource()'s callback queue (the thing that gets
//     stuck), this loads sku-drawer.js directly via
//     ReactLoader.loadPromise() and polls for window.quickShop to become
//     available before opening the drawer.
;(function () {
  function loadOnce(id) {
    var flag = '__cartFixLoading_' + id
    if (window[flag]) return
    window[flag] = true
    if (window.ReactLoader && typeof window.ReactLoader.loadPromise === 'function') {
      try {
        window.ReactLoader.loadPromise(id).catch(function () {})
      } catch (err) {}
    }
    // Safety net: if loadPromise() never settles (it can hang even though the
    // underlying <script> does load), don't leave this flag stuck forever -
    // clear it after a few seconds so a later click can retry the load.
    setTimeout(function () {
      window[flag] = false
    }, 10000)
  }

  function waitFor(check, done, elapsed) {
    elapsed = elapsed || 0
    if (check()) {
      done()
      return
    }
    if (elapsed >= 10000) return // give up quietly rather than error
    setTimeout(function () {
      waitFor(check, done, elapsed + 100)
    }, 100)
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('.hxz-cart-btn')
      if (!btn) return
      var card = btn.closest('hxz-product-card')
      if (!card || typeof card.addToCart !== 'function') return

      e.stopImmediatePropagation()
      e.preventDefault()

      // GCC: ignore an accidental double tap (prevents adding the item twice)
      var now = Date.now()
      if (card.__lastQuickAdd && now - card.__lastQuickAdd < 1500) return
      card.__lastQuickAdd = now

      if (card.dataset.multiple === '1') {
        if (typeof window.quickShop === 'function') {
          card.addToCart()
          return
        }
        if (!window.React || !window.ReactDOM || !window.ReactDOM.createRoot) {
          loadOnce('react')
          loadOnce('react-dom')
        }
        waitFor(
          function () {
            return !!(window.React && window.ReactDOM && window.ReactDOM.createRoot)
          },
          function () {
            loadOnce('sku-drawer')
            waitFor(
              function () {
                return typeof window.quickShop === 'function'
              },
              function () {
                card.addToCart()
              }
            )
          }
        )
      } else {
        card.addToCart()
      }
    },
    true
  )
})()

// Hotfix #2: the Quick Add drawer (assets/sku-drawer.js) loads its product
// data from a third-party backend at /a/choogo/lts/api/v1/product/detail.
// That backend is gone - Shopify's storefront now returns the exact same
// empty 404 for that path as it does for a made-up, never-installed app
// proxy prefix, which means whatever app used to serve "/a/choogo/..." has
// been uninstalled. sku-drawer.js doesn't handle that failure: it assumes
// the request always succeeds, so when it 404s the drawer opens, throws
// "Cannot read properties of undefined (reading 'variants')" internally,
// and is left stuck on its gray loading skeleton forever.
//
// Rather than patch the bundled/minified sku-drawer.js itself, this
// intercepts just that one endpoint at the fetch layer and serves
// equivalent data straight from Shopify's own built-in product JSON
// endpoint (/products/<handle>.js), reshaped into the same {code, data}
// envelope sku-drawer.js expects. This runs at page load, before
// sku-drawer.js is ever lazy-loaded, so the override is always in place
// by the time a Quick Add click needs it.
;(function () {
  if (window.__choogoProductDetailFallback) return
  window.__choogoProductDetailFallback = true

  var nativeFetch = window.fetch.bind(window)
  var DETAIL_RE = /\/a\/choogo\/lts\/api\/v1\/product\/detail\?productId=(\d+)/

  function findHandleForProductId(productId) {
    var card = document.querySelector('hxz-product-card[data-product-id="' + productId + '"]')
    if (!card) return null
    var link = card.querySelector('a[href*="/products/"]')
    if (!link) return null
    var match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)
    return match ? match[1] : null
  }

  // GCC: products whose sold-out shades are hidden everywhere (Serum Touch foundation)
  var HIDE_SOLD_OUT_IDS = ['8357441339531']

  function toChoogoShape(product) {
    var hideSoldOut = HIDE_SOLD_OUT_IDS.indexOf(String(product.id)) !== -1
    var mainImage = product.image
      ? { src: product.image.src }
      : product.images && product.images[0]
      ? { src: typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src }
      : null

    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      vendor: product.vendor,
      image: mainImage,
      images: (product.images || []).map(function (img) {
        return {
          src: typeof img === 'string' ? img : img.src,
          variant_ids: (typeof img === 'object' && img.variant_ids) || [],
        }
      }),
      // GCC: for the foundation, only offer in-stock shades in the Quick Add drawer;
      // other products show all shades (sold-out ones can't be added).
      variants: (product.variants || []).filter(function (v) {
        return !hideSoldOut || v.available !== false
      }).map(function (v) {
        var qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : v.available ? 1 : 0
        var price = v.price / 100
        return {
          id: String(v.id),
          title: v.title,
          sku: v.sku,
          price: price,
          // sku-drawer.js reads the display price from
          // variant.pricingInCountry.price.amount (falling back to
          // variant.price only if that whole chain is present but empty) -
          // it doesn't optional-chain past `pricingInCountry` itself, so
          // without this it throws "Cannot read properties of undefined
          // (reading 'price')" the moment it tries to render.
          pricingInCountry: { price: { amount: price } },
          inventory_quantity: qty,
        }
      }),
    }
  }

  // The drawer also calls this second endpoint (for variant swatch colors
  // and for engraving metadata). It's not needed to display real product
  // data, but leaving it unhandled means every call 404s with an empty
  // body, res.json() throws on the empty string, and sku-drawer.js reads
  // straight into the result (`e.data.data...`) without checking `e`
  // itself first - an unhandled exception on every drawer open. Returning
  // a validly-shaped "nothing found" response lets those optional
  // swatch/engraving features degrade quietly instead.
  var METAFIELD_RE = /\/a\/choogo\/lts\/api\/v1\/product\/metafield\/variant\/detail\?/

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || ''

    if (METAFIELD_RE.test(url)) {
      var emptyBody = JSON.stringify({
        data: { data: { product: { metafields: { edges: [] }, variants: { edges: [] } } } },
      })
      return Promise.resolve(
        new Response(emptyBody, { status: 200, headers: { 'content-type': 'application/json' } })
      )
    }

    var match = url.match(DETAIL_RE)
    if (!match) return nativeFetch(input, init)

    var productId = match[1]
    var handle = findHandleForProductId(productId)
    if (!handle) return nativeFetch(input, init)

    return nativeFetch('/products/' + handle + '.js')
      .then(function (res) {
        if (!res.ok) return nativeFetch(input, init)
        return res.json().then(function (product) {
          var body = JSON.stringify({ code: 200, data: toChoogoShape(product) })
          return new Response(body, { status: 200, headers: { 'content-type': 'application/json' } })
        })
      })
      .catch(function () {
        return nativeFetch(input, init)
      })
  }
})()

// GCC: warm up Quick Add in the background. Previously React, react-dom and
// the shade drawer (sku-drawer.js) were only downloaded on the FIRST tap of
// "+ Quick Shop", so that tap looked like it did nothing for a few seconds on
// mobile. Now they load (in order) after the page is idle or on the first
// scroll/touch, so the drawer opens on the first tap.
;(function () {
  var started = false
  function load(id) {
    window['__cartFixLoading_' + id] = true // share with the click handler above
    try {
      if (window.ReactLoader && typeof window.ReactLoader.loadPromise === 'function') {
        window.ReactLoader.loadPromise(id).catch(function () {})
      }
    } catch (err) {}
  }
  var asked = {}
  function step(n) {
    if (n > 120 || typeof window.quickShop === 'function') return
    var id = !window.React
      ? 'react'
      : !window.ReactDOM || !window.ReactDOM.createRoot
      ? 'react-dom'
      : 'sku-drawer'
    if (!asked[id]) {
      asked[id] = true
      load(id)
    }
    setTimeout(function () {
      step(n + 1)
    }, 150)
  }
  function warm() {
    if (started) return
    started = true
    step(0)
  }
  function onFirst() {
    ;['touchstart', 'scroll', 'mousemove'].forEach(function (ev) {
      window.removeEventListener(ev, onFirst)
    })
    warm()
  }
  ;['touchstart', 'scroll', 'mousemove'].forEach(function (ev) {
    window.addEventListener(ev, onFirst, { passive: true })
  })
  function idle() {
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 })
    else setTimeout(warm, 3000)
  }
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle)
})()

// GCC: the Serum Touch foundation hides sold-out shades (see hxz-new.css), so
// make its "All shades (N)" counter show only the shades a customer can pick.
// Other products show every shade, so their counter is left as is.
;(function () {
  function fix() {
    document.querySelectorAll('hxz-variant-block[data-product-id="8357441339531"]').forEach(function (block) {
      var counter = block.querySelector('.variants-size')
      if (!counter) return
      var available = block.querySelectorAll('hxz-variant-option:not([data-available="false"])').length
      counter.textContent = available
      if (available <= 7) {
        var toggle = block.querySelector('.variant-toggle')
        if (toggle) toggle.style.display = 'none'
      }
    })
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fix)
  else fix()
})()
